import { AxiosError } from "axios";
import uniq from "lodash/uniq";

import type {
  QueryFunction,
  QueryFunctionContext,
  QueryKeyPrefix,
  FetcherQueryKey,
  AnyState,
  AnyStateWithEnsuredData,
  State,
  StateWithEnsuredData,
  MutationState,
  MutationEnvelope,
  QueryEnvelope,
  EnsuredQueryEnvelope,
} from "./helpers/query.types";
import {
  QueryStatus,
  ensureErrorState,
  ensureErrorStateWithoutData,
  ensureErrorStateWithData,
  ensureIdleState,
  ensureLoadingState,
  ensureSuccessState,
  queryClient,
  queryClientDefaultOptions,
  validQueryStatuses,
} from "./helpers/query.helpers";

export { registerOnQueryRemoveCallback } from "./helpers/query.helpers.onRemove";

export type {
  QueryStatus,
  QueryFunction,
  QueryFunctionContext,
  QueryKeyPrefix,
  FetcherQueryKey,
  AnyState,
  AnyStateWithEnsuredData,
  State,
  StateWithEnsuredData,
  MutationState,
  MutationEnvelope,
  QueryEnvelope,
  EnsuredQueryEnvelope,
};

export { queryClient, queryClientDefaultOptions };

export const ensureState = <
  Data,
  ErrorT extends NonNullable<Error> = AxiosError,
>(
  state: AnyState<Data, ErrorT>,
  allowUndefinedData?: boolean,
): State<Data, ErrorT> => {
  const { status } = state;
  if (status === "idle") {
    return ensureIdleState(state);
  }
  if (status === "loading") {
    return ensureLoadingState(state);
  }
  if (status === "success") {
    return ensureSuccessState(state, allowUndefinedData);
  }
  if (status === "error") {
    return ensureErrorState(state);
  }
  throw new Error("Invalid status");
};

export const ensureStateWithData = <
  Data,
  ErrorT extends NonNullable<Error> = AxiosError,
>(
  state: AnyState<Data, ErrorT>,
  allowUndefinedData?: boolean,
): StateWithEnsuredData<Data, ErrorT> => {
  const { status } = state;
  if (status === "idle") {
    throw new Error("idle state should not contain data");
  }
  if (status === "loading") {
    throw new Error("loading state should not contain data");
  }
  if (status === "success") {
    return ensureSuccessState(state, allowUndefinedData);
  }
  if (status === "error") {
    return ensureErrorStateWithData(state, allowUndefinedData);
  }
  throw new Error("Invalid status");
};

export const ensureMutationState = <
  Data,
  ErrorT extends NonNullable<Error> = AxiosError,
>(
  state: AnyState<Data, ErrorT>,
  allowUndefinedData?: boolean,
): MutationState<Data, ErrorT> => {
  const { status } = state;
  if (status === "idle") {
    return ensureIdleState(state);
  }
  if (status === "loading") {
    return ensureLoadingState(state);
  }
  if (status === "success") {
    return ensureSuccessState(state, allowUndefinedData);
  }
  if (status === "error") {
    return ensureErrorStateWithoutData(state);
  }
  throw new Error("Invalid status");
};

export const computeIsQueryEnabled = (
  fetcherParams: Record<string, any>,
  {
    forceDisable,
  }: {
    forceDisable?: boolean;
  } = {},
): boolean => {
  if (forceDisable) {
    return false;
  }
  return Object.values(fetcherParams).every(
    (x) => x !== null && x !== undefined,
  );
};

export const retryUnless404 = (
  failureCount: number,
  error: AxiosError,
): boolean => {
  if (error.response?.status === 404) {
    // Don't retry on 404s. Instead, handle this immediately in onError.
    return false;
  }
  // Otherwise, retry twice. (This is the default)
  return failureCount < 2;
};

export const consolidateStatuses = (...s: QueryStatus[]): QueryStatus => {
  const statuses = uniq(s);

  if (statuses.length === 0) {
    throw new Error("consolidateStatuses requires at least one status");
  }
  statuses.forEach((status) => {
    if (!validQueryStatuses.includes(status)) {
      throw new Error(
        `consolidateStatuses received an invalid status "${status}"`,
      );
    }
  });

  if (statuses.length === 1) {
    return s[0];
  }
  if (statuses.includes("loading")) {
    return "loading";
  }
  if (statuses.includes("error")) {
    return "error";
  }
  return "loading";
};
